import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  StickyNote,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  AlertTriangle,
  Flag,
  MoreVertical,
  Loader2,
  Mic,
  MicOff,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Priority configuration with post-it colors
const PRIORITY_CONFIG = {
  low: { 
    label: 'Faible', 
    color: 'bg-green-100 border-green-300',
    textColor: 'text-green-800',
    bgGradient: 'from-green-50 to-green-100'
  },
  medium: { 
    label: 'Moyenne', 
    color: 'bg-yellow-100 border-yellow-300',
    textColor: 'text-yellow-800',
    bgGradient: 'from-yellow-50 to-yellow-100'
  },
  high: { 
    label: 'Haute', 
    color: 'bg-orange-100 border-orange-300',
    textColor: 'text-orange-800',
    bgGradient: 'from-orange-50 to-orange-100'
  },
  urgent: { 
    label: 'Urgent', 
    color: 'bg-red-100 border-red-300',
    textColor: 'text-red-800',
    bgGradient: 'from-red-50 to-red-100'
  }
};

const QuickNotesWidget = ({ maxNotes = 6, showTitle = true }) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'medium'
  });

  const fetchNotes = useCallback(async () => {
    try {
      const res = await api.get('/quick-notes/');
      setNotes(res.data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const resetForm = () => {
    setFormData({ title: '', content: '', priority: 'medium' });
    setEditingNote(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title || '',
      content: note.content || '',
      priority: note.priority || 'medium'
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.content.trim()) {
      toast.error('Veuillez écrire une note');
      return;
    }

    setSaving(true);
    try {
      if (editingNote) {
        await api.put(`/quick-notes/${editingNote.id}`, formData);
        toast.success('Note mise à jour');
      } else {
        await api.post('/quick-notes/', formData);
        toast.success('Note créée');
      }
      setShowDialog(false);
      resetForm();
      fetchNotes();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    setDeleting(noteId);
    try {
      await api.delete(`/quick-notes/${noteId}`);
      toast.success('Note supprimée');
      fetchNotes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Parlez maintenant...');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', audioBlob, 'recording.webm');
      
      const response = await api.post('/quick-notes/transcribe', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data?.transcript) {
        setFormData(prev => ({
          ...prev,
          content: prev.content ? `${prev.content}\n${response.data.transcript}` : response.data.transcript
        }));
        toast.success('Transcription réussie !');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Display only latest notes
  const displayedNotes = notes.slice(0, maxNotes);

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-full" data-testid="quick-notes-widget">
        {showTitle && (
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-yellow-500" />
              Bloc-notes rapide
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={openCreateDialog}>
              <Plus className="w-4 h-4" />
            </Button>
          </CardHeader>
        )}
        <CardContent className={cn(!showTitle && "pt-4")}>
          {notes.length === 0 ? (
            <div className="text-center py-6">
              <StickyNote className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500 mb-3">Aucune note</p>
              <Button size="sm" variant="outline" onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-1" />
                Créer une note
              </Button>
            </div>
          ) : (
            <>
              {/* Post-it Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {displayedNotes.map((note) => {
                  const priorityConfig = PRIORITY_CONFIG[note.priority] || PRIORITY_CONFIG.medium;
                  
                  return (
                    <div
                      key={note.id}
                      className={cn(
                        "relative p-3 rounded-lg border-2 shadow-sm hover:shadow-md transition-shadow cursor-pointer group min-h-[120px]",
                        "bg-gradient-to-br",
                        priorityConfig.bgGradient,
                        priorityConfig.color
                      )}
                      onClick={() => openEditDialog(note)}
                      style={{
                        transform: `rotate(${Math.random() * 2 - 1}deg)`
                      }}
                      data-testid={`note-${note.id}`}
                    >
                      {/* Priority indicator */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-6 w-6">
                              <MoreVertical className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(note); }}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }}
                              disabled={deleting === note.id}
                            >
                              {deleting === note.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                              )}
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      {/* Title */}
                      {note.title && (
                        <p className={cn("font-semibold text-sm mb-1 line-clamp-1", priorityConfig.textColor)}>
                          {note.title}
                        </p>
                      )}
                      
                      {/* Content */}
                      <p className={cn("text-xs line-clamp-3 mb-2", priorityConfig.textColor, "opacity-80")}>
                        {note.content}
                      </p>
                      
                      {/* Footer */}
                      <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between">
                        <span className={cn("text-[10px] flex items-center gap-1", priorityConfig.textColor, "opacity-60")}>
                          <Calendar className="w-3 h-3" />
                          {formatDate(note.created_at)}
                        </span>
                        {note.priority === 'urgent' && (
                          <Flag className="w-3 h-3 text-red-500" />
                        )}
                        {note.priority === 'high' && (
                          <AlertTriangle className="w-3 h-3 text-orange-500" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* View all link */}
              {notes.length > maxNotes && (
                <div className="mt-3 text-center">
                  <Button variant="link" size="sm" className="text-xs">
                    Voir toutes les notes ({notes.length})
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Note Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-yellow-500" />
              {editingNote ? 'Modifier la note' : 'Nouvelle note'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Titre (optionnel)</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Titre de la note..."
              />
            </div>

            {/* Content with voice */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Contenu *</label>
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "outline"}
                  size="sm"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isTranscribing}
                >
                  {isTranscribing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Transcription...
                    </>
                  ) : isRecording ? (
                    <>
                      <MicOff className="w-4 h-4 mr-1" />
                      Arrêter
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-1" />
                      Dicter
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Écrivez votre note ou utilisez la dictée vocale..."
                rows={4}
              />
              {isRecording && (
                <div className="flex items-center gap-2 text-red-500 text-sm animate-pulse">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-ping" />
                  Enregistrement en cours...
                </div>
              )}
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Importance</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, priority: key }))}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium transition-all border-2",
                      formData.priority === key
                        ? cn(config.color, config.textColor, "ring-2 ring-offset-1")
                        : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                    )}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !formData.content.trim()}
              className="bg-yellow-500 hover:bg-yellow-600 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingNote ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickNotesWidget;
